/**
 * O rodapé da sidebar: quem sou eu, e os dois botões que se carregam cem vezes
 * por dia (microfone e auscultadores). Ficam sempre no mesmo pixel — memória
 * muscular vale mais do que qualquer animação.
 */

import { SELECTABLE_STATUSES } from "@greenvoice/shared";
import { useEffect, useRef, useState } from "react";
import { micControl, useVoiceActions, useVoiceSnapshot, voiceLive } from "../hooks/useVoice";
import { useStore } from "../store/store";
import { Avatar, STATUS_LABEL } from "./Avatar";
import { IconEdit, IconHeadphones, IconHeadphonesOff, IconMic, IconMicOff, IconSettings } from "./Icons";
import { ProfileCard } from "./ProfileCard";
import { Selo } from "./Selo";
import { UserSettings } from "./UserSettings";

export function UserPanel() {
  const self = useStore((s) => s.self);
  const setPresence = useStore((s) => s.setPresence);
  const signOut = useStore((s) => s.signOut);
  const capabilities = useStore((s) => s.voice?.capabilities);

  const snapshot = useVoiceSnapshot();
  const actions = useVoiceActions();

  const [menuOpen, setMenuOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!menuOpen) return;
    const close = (event: MouseEvent) => {
      if (!menuRef.current?.contains(event.target as Node)) setMenuOpen(false);
    };
    // `capture` para fechar mesmo quando o clique é apanhado por outro handler.
    document.addEventListener("mousedown", close, true);
    return () => document.removeEventListener("mousedown", close, true);
  }, [menuOpen]);

  if (!self) return null;

  // Fora de uma sala não há nada para silenciar: os botões ficam desligados em
  // vez de escondidos, para a barra não saltar quando se entra numa chamada.
  //
  // "Numa chamada" é o MOTOR estar ligado, e não `store.voice !== null`: o
  // huddle de corredor põe voz a andar sem criar sessão na API, e com o teste
  // antigo estes dois botões ficavam mortos exactamente na chamada em que se
  // entra sem carregar em nada — dava para falar e não dava para calar.
  const live = voiceLive(snapshot);
  const micOn = live && snapshot.micEnabled;
  const deafened = snapshot.deafened;
  // A regra do botão vem de fora: aqui estava escrita uma TERCEIRA variante, que
  // nem sequer olhava para a permissão de falar. Ver `micControl`.
  const mic = micControl(snapshot, capabilities);

  return (
    <div className="user-panel" ref={menuRef}>
      <button type="button" className="user-panel__me" onClick={() => setMenuOpen((v) => !v)}>
        <Avatar user={self} size={32} showStatus />
        <span className="user-panel__names">
          {/* Sem selo de microfone ao pé do nome: «esse microfone do nome, no
              greenvoice, não precisa, apenas no office 2d».

              Aqui não faz falta pela mesma razão que fazia falta no boneco: o
              botão do microfone está a dois centímetros daqui, na mesma linha,
              já vermelho e já com o ícone riscado. No escritório 2D o boneco não
              tem botão nenhum ao lado, e o selo é a única coisa que responde;
              nesta coluna era a terceira vez que a mesma resposta era dada. */}
          <strong>{self.displayName ?? self.username}</strong>
          <small>{self.statusText ?? STATUS_LABEL[self.status]}</small>
        </span>
      </button>

      <div className="user-panel__actions">
        <button
          type="button"
          className={`icon-button${micOn ? "" : " is-off"}`}
          disabled={mic.disabled}
          onClick={() => void actions.toggleMic()}
          title={mic.title}
        >
          {micOn ? <IconMic size={18} /> : <IconMicOff size={18} />}
        </button>
        <button
          type="button"
          className={`icon-button${deafened ? " is-off" : ""}`}
          disabled={!live}
          onClick={() => void actions.toggleDeaf()}
          title={deafened ? "Voltar a ouvir" : "Ensurdecer"}
        >
          {deafened ? <IconHeadphonesOff size={18} /> : <IconHeadphones size={18} />}
        </button>
        <button type="button" className="icon-button" onClick={() => setSettingsOpen(true)} title="Configurações">
          <IconSettings size={18} />
        </button>
      </div>

      {settingsOpen ? <UserSettings onClose={() => setSettingsOpen(false)} /> : null}

      {menuOpen ? (
        <div className="user-menu" role="menu">
          {/* O PERFIL primeiro, e é o mesmo cartão que os outros veem quando
              carregam no meu nome numa mensagem — carregar no próprio nome e ver
              uma lista de estados era a app a responder outra coisa que não a
              pergunta feita. Os estados continuam por baixo: quem vinha por eles
              tem-nos no mesmo sítio, a um palmo. */}
          <ProfileCard user={self} />
          <button
            type="button"
            role="menuitem"
            className="user-menu__item user-menu__item--editar"
            onClick={() => {
              setMenuOpen(false);
              setSettingsOpen(true);
            }}
          >
            <IconEdit size={15} />
            Editar perfil
          </button>
          <hr />
          {/* A lista vem do pacote partilhado: era a quarta cópia da mesma
              enumeração, e a API valida contra a dela. Duas listas a divergir
              davam um botão que o servidor recusa — ou pior, que ele aceita. */}
          {SELECTABLE_STATUSES.map((status) => (
            <button
              key={status}
              type="button"
              role="menuitem"
              className={`user-menu__item${self.status === status ? " is-active" : ""}`}
              onClick={() => {
                setPresence(status);
                setMenuOpen(false);
              }}
            >
              <Selo status={status} className="user-menu__selo" />
              {STATUS_LABEL[status]}
            </button>
          ))}
          <hr />
          <button
            type="button"
            role="menuitem"
            className="user-menu__item user-menu__item--danger"
            onClick={() => void signOut()}
          >
            Sair
          </button>
        </div>
      ) : null}
    </div>
  );
}
